import React, { useState, useEffect } from 'react';
import api from '../utils/api';

function Kategorien() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
    color: '#2F80FF',
    icon: ''
  });

  const loadCategories = async () => {
    try {
      setLoading(true);
      const data = await api.get('/api/products/categories');
      setCategories(data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler beim Laden: ' + err.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Name ist erforderlich' });
      return;
    }

    try {
      await api.post('/api/products/categories', {
        ...formData,
        sort_order: categories.length
      });
      setMessage({ type: 'success', text: '✓ Kategorie erstellt' });
      setShowCreateModal(false);
      setFormData({ name: '', is_active: true, color: '#2F80FF', icon: '' });
      loadCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const handleUpdate = async (categoryId, updates) => {
    try {
      await api.patch(`/api/products/categories/${categoryId}`, updates);
      setMessage({ type: 'success', text: '✓ Gespeichert' });
      loadCategories();
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const handleToggleActive = async (category) => {
    await handleUpdate(category.category_id, { is_active: !category.is_active });
  };

  const handleDelete = async (categoryId, productCount) => {
    if (productCount > 0) {
      setMessage({ 
        type: 'error', 
        text: `Kategorie kann nicht gelöscht werden: ${productCount} Produkte zugeordnet` 
      });
      return;
    }

    if (!window.confirm('Kategorie wirklich löschen?')) return;

    try {
      await api.delete(`/api/products/categories/${categoryId}`);
      setMessage({ type: 'success', text: '✓ Kategorie gelöscht' });
      loadCategories();
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
    }
  };

  const moveCategory = async (index, direction) => {
    const newCategories = [...categories];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= newCategories.length) return;
    
    [newCategories[index], newCategories[targetIndex]] = 
    [newCategories[targetIndex], newCategories[index]];
    
    setCategories(newCategories);
    
    try {
      await api.post('/api/products/categories/reorder', {
        category_ids: newCategories.map(c => c.category_id)
      });
      setMessage({ type: 'success', text: '✓ Reihenfolge gespeichert' });
      setTimeout(() => setMessage(null), 2000);
    } catch (err) {
      setMessage({ type: 'error', text: 'Fehler: ' + err.message });
      loadCategories();
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <div style={{ color: 'var(--text-secondary)' }}>Lade Kategorien...</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <div className="flex justify-between items-start mb-6 lg:mb-8 flex-wrap gap-3">
          <div className="flex-1 min-w-[200px]">
            <h1 className="page-title text-xl lg:text-2xl">Kategorien</h1>
            <p className="page-subtitle text-sm lg:text-base">Produktkategorien verwalten und sortieren</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary text-sm lg:text-base whitespace-nowrap"
          >
            + Neue Kategorie
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={message.type === 'success' ? 'alert alert-success' : 'alert alert-error'}>
          {message.text}
        </div>
      )}

      {/* Categories List */}
      {categories.length === 0 ? (
        <div className="dark-card" style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--text-secondary)' }}>
          <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>🏷️</div>
          <div style={{ fontSize: '1.125rem', marginBottom: 'var(--space-2)' }}>Keine Kategorien vorhanden</div>
          <div style={{ fontSize: '0.875rem' }}>Erstellen Sie Ihre erste Kategorie</div>
        </div>
      ) : (
        <>
          {/* Desktop Table */}
          <div className="dark-card hidden lg:block">
            <div className="dark-table-container">
              <table className="dark-table">
              <thead>
                <tr>
                  <th style={{ width: '60px' }}>Pos.</th>
                  <th style={{ width: '60px' }}>Icon</th>
                  <th>Name</th>
                  <th style={{ width: '120px' }}>Produkte</th>
                  <th style={{ width: '120px' }}>Status</th>
                  <th style={{ width: '200px', textAlign: 'right' }}>Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((category, index) => (
                  <tr key={category.category_id}>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
                        <button
                          onClick={() => moveCategory(index, 'up')}
                          disabled={index === 0}
                          className="btn-secondary"
                          style={{ padding: 'var(--space-1)', fontSize: '0.75rem', minHeight: 'auto' }}
                        >
                          ↑
                        </button>
                        <button
                          onClick={() => moveCategory(index, 'down')}
                          disabled={index === categories.length - 1}
                          className="btn-secondary"
                          style={{ padding: 'var(--space-1)', fontSize: '0.75rem', minHeight: 'auto' }}
                        >
                          ↓
                        </button>
                      </div>
                    </td>
                    <td style={{ fontSize: '1.5rem' }}>
                      {category.icon || '📦'}
                    </td>
                    <td>
                      <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>
                        {category.name}
                      </div>
                    </td>
                    <td>
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {category.product_count || 0}
                      </span>
                    </td>
                    <td>
                      {category.is_active ? (
                        <span className="badge badge-success">Aktiv</span>
                      ) : (
                        <span className="badge badge-neutral">Inaktiv</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
                        <button
                          onClick={() => handleToggleActive(category)}
                          className="btn-secondary"
                          style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                        >
                          {category.is_active ? 'Deaktivieren' : 'Aktivieren'}
                        </button>
                        <button
                          onClick={() => setEditingCategory(category)}
                          className="btn-secondary"
                          style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                        >
                          Bearbeiten
                        </button>
                        <button
                          onClick={() => handleDelete(category.category_id, category.product_count)}
                          className="btn-danger"
                          style={{ padding: 'var(--space-2) var(--space-3)', fontSize: '0.875rem' }}
                          disabled={category.product_count > 0}
                        >
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-3">
          {categories.map((category, index) => (
            <div key={category.category_id} className="dark-card p-4">
              <div className="flex items-start gap-3 mb-3">
                <div className="text-4xl">{category.icon || '📦'}</div>
                <div className="flex-1">
                  <h3 className="font-semibold text-base text-[var(--text-primary)] mb-1">
                    {category.name}
                  </h3>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {category.product_count || 0} Produkte
                  </p>
                </div>
                {category.is_active ? (
                  <span className="badge badge-success text-xs">Aktiv</span>
                ) : (
                  <span className="badge badge-neutral text-xs">Inaktiv</span>
                )}
              </div>
              
              {/* Sort Buttons */}
              <div className="flex gap-2 mb-3 pb-3 border-b border-[var(--border-primary)]">
                <button
                  onClick={() => moveCategory(index, 'up')}
                  disabled={index === 0}
                  className="btn-secondary flex-1 text-sm py-2 disabled:opacity-30"
                >
                  ↑ Hoch
                </button>
                <button
                  onClick={() => moveCategory(index, 'down')}
                  disabled={index === categories.length - 1}
                  className="btn-secondary flex-1 text-sm py-2 disabled:opacity-30"
                >
                  ↓ Runter
                </button>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <button
                  onClick={() => handleToggleActive(category)}
                  className="btn-secondary flex-1 text-sm py-3"
                >
                  {category.is_active ? '🔴 Deaktivieren' : '🟢 Aktivieren'}
                </button>
                <button
                  onClick={() => setEditingCategory(category)}
                  className="btn-primary flex-1 text-sm py-3"
                >
                  ✏️ Bearbeiten
                </button>
                <button
                  onClick={() => handleDelete(category.category_id, category.product_count)}
                  disabled={category.product_count > 0}
                  className="btn-danger text-sm py-3 px-4 disabled:opacity-30"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      </>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 'var(--space-6)', color: 'var(--text-primary)' }}>
              Neue Kategorie
            </h2>
            
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                className="dark-input"
                placeholder="z.B. Pizza"
              />
            </div>

            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Icon (optional)
              </label>
              <input
                type="text"
                value={formData.icon}
                onChange={e => setFormData({ ...formData, icon: e.target.value })}
                className="dark-input"
                placeholder="🍕"
              />
            </div>

            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ width: '16px', height: '16px' }}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--text-primary)' }}>
                  Aktiv
                </span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                onClick={() => setShowCreateModal(false)}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Abbrechen
              </button>
              <button
                onClick={handleCreate}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Erstellen
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingCategory && (
        <div className="modal-overlay" onClick={() => setEditingCategory(null)}>
          <div className="modal-container" onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: 'var(--space-6)', color: 'var(--text-primary)' }}>
              Kategorie bearbeiten
            </h2>
            
            <div style={{ marginBottom: 'var(--space-4)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Name
              </label>
              <input
                type="text"
                value={editingCategory.name}
                onChange={e => setEditingCategory({ ...editingCategory, name: e.target.value })}
                className="dark-input"
              />
            </div>

            <div style={{ marginBottom: 'var(--space-6)' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 500, marginBottom: 'var(--space-2)', color: 'var(--text-primary)' }}>
                Icon
              </label>
              <input
                type="text"
                value={editingCategory.icon || ''}
                onChange={e => setEditingCategory({ ...editingCategory, icon: e.target.value })}
                className="dark-input"
              />
            </div>

            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
              <button
                onClick={() => setEditingCategory(null)}
                className="btn-secondary"
                style={{ flex: 1 }}
              >
                Abbrechen
              </button>
              <button
                onClick={async () => {
                  await handleUpdate(editingCategory.category_id, {
                    name: editingCategory.name,
                    icon: editingCategory.icon
                  });
                  setEditingCategory(null);
                }}
                className="btn-primary"
                style={{ flex: 1 }}
              >
                Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Kategorien;
